"use client";

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeftOutlined, ArrowRightOutlined, CloseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import uploadIcon from "../../../assets/images/upload.png";
import chooseImage from "../../../assets/icons/tswira.png";
import icon1 from "../../../assets/images/logomedilink.svg";

import "../../../authPatient/pages/SignupPatient/SignupPatient.css";
import "./RegisterForm3.css";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
// doit correspondre à la durée de transition CSS (0.5s)
const SWAP_DURATION = 500;
// clé localStorage partagée entre toutes les pages auth (patient/doctor)
const SWAP_STORAGE_KEY = "authHeroOnLeft";
// layout d'origine de cette page : hero à gauche (classe signup-doctor)
const DEFAULT_HERO_ON_LEFT = true;

export default function RegisterForm3() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState(null);

  // ── état persistant de la position des cartes (hero à gauche ou à droite) ──
  const initialHeroOnLeft = (() => {
    if (typeof location.state?.heroOnLeft === "boolean") {
      return location.state.heroOnLeft;
    }
    const stored = typeof window !== "undefined" ? localStorage.getItem(SWAP_STORAGE_KEY) : null;
    return stored === null ? DEFAULT_HERO_ON_LEFT : stored === "true";
  })();

  const [heroOnLeft, setHeroOnLeft] = useState(initialHeroOnLeft);

  // ── refs + état pour le swap des 2 cards (form <-> hero) ──
  const heroRef = useRef(null);
  const formWrapperRef = useRef(null);
  const [isSwapping, setIsSwapping] = useState(false);
  const [swapStyle, setSwapStyle] = useState({ form: 0, hero: 0 });

  const handleContinueAsPatient = () => {
    if (isSwapping) return;

    const formEl = formWrapperRef.current;
    const heroEl = heroRef.current;

    if (formEl && heroEl) {
      const formRect = formEl.getBoundingClientRect();
      const heroRect = heroEl.getBoundingClientRect();
      const formDeltaX = heroRect.left - formRect.left;
      const heroDeltaX = formRect.left - heroRect.left;
      setSwapStyle({ form: formDeltaX, hero: heroDeltaX });
    }

    setIsSwapping(true);

    const newHeroOnLeft = !heroOnLeft;

    setTimeout(() => {
      localStorage.setItem(SWAP_STORAGE_KEY, String(newHeroOnLeft));
      navigate("/LoginPatient", { state: { heroOnLeft: newHeroOnLeft } });
    }, SWAP_DURATION);
  };

  const validateFile = useCallback(
    (f) => {
      if (!f) return t("signup.doctor.fileErrorNone");
      if (!["image/jpeg", "image/png"].includes(f.type))
        return t("signup.doctor.fileErrorType");
      if (f.size > MAX_FILE_SIZE) return t("signup.doctor.fileErrorSize");
      return null;
    },
    [t]
  );

  const applyFile = (f) => {
    const err = validateFile(f);
    setError(err);
    if (!err) {
      setFile(f);
    } else {
      setFile(null);
    }
  };

  const onChangeInput = (e) => {
    const f = e.target.files?.[0];
    if (f) applyFile(f);
    e.target.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) applyFile(f);
  };
  const onDragOver = (e) => e.preventDefault();

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handleRemove = () => {
    setFile(null);
    setError(null);
  };

  const isPage3Valid = !!file;

  return (
    <div className="signup-container signup-doctor-auth">
      <div
        className={`signup-background signup-doctor ${
          heroOnLeft ? "pos-hero-left" : "pos-form-left"
        }`}
      >
        <aside
          className="signup-hero"
          ref={heroRef}
          style={{
            backgroundImage: `url(${chooseImage})`,
            ...(isSwapping
              ? { transform: `translateX(${swapStyle.hero}px)`, opacity: 0.15 }
              : {}),
          }}
        >
          <div className="signup-hero-glow" />
          <button
            type="button"
            className="signup-back-home signup-back-home-doctor signup-hero-back-btn"
            onClick={() => navigate("/")}
            aria-label={t("common.backToHome", "Back to home page")}
          >
            <ArrowLeftOutlined />
            <span>{t("common.backToHome", "Back to home page")}</span>
          </button>
          
          <div className="signup-hero-top-icon">
            <img src={icon1} alt="" />
          </div>

     
          <div className="signup-hero-inner">
            <h2 className="signup-hero-title">
              {t(
                "signup.createAccountHeroTitle",
                "Are you signing up as a patient? This registration is for doctors only."
              )}
            </h2>
            <p className="signup-hero-sub">
              {t(
                "signup.doctor.step3HeroText",
                "To book appointments and access care, please create a patient account instead."
              )}
            </p>
            <button
              type="button"
              className="signup-hero-cta"
              onClick={handleContinueAsPatient}
            >
              <span>{t("signup.continueAsPatient", "Continue as a patient")}</span>
              <ArrowRightOutlined />
            </button>
          </div>
        </aside>

        <section
          className="signup-form-wrapper"
          ref={formWrapperRef}
          style={
            isSwapping
              ? { transform: `translateX(${swapStyle.form}px)`, opacity: 0.15 }
              : undefined
          }
        >
          <div className="signup-form">
            {/* ── Header : titre + step label ── */}
            <div className="signup-form-header">
              <h1 className="signup-form-title-main">
                {t("signup.createAccountTitle1", "Create Your")}{" "}
                <span className="signup-form-title-accent">
                  {t("signup.createAccountTitle2", "Doctor Account")}
                </span>
              </h1>
            
            </div>

            <div className="signup-form-content">
              {/* ── Progress bar ── */}
              <div className="rf3-steps">
                <span className="rf3-step rf3-step--done" />
                <span className="rf3-step rf3-step--done" />
                <span className="rf3-step rf3-step--active" />
                <span className="rf3-step" />
              </div>
  <p className="rf3-step-label">
                {t("signup.stepLabel3", "Step 3 / 4")}
              </p>
              {/* ── Titre de section + description ── */}
              <div className="rf3-section">
                <h2 className="rf3-heading">
                  {t("signup.doctor.step3Heading", "VERIFY YOUR MEDICAL LICENSE")}
                </h2>
                <p className="rf3-text">
                  {t(
                    "signup.doctor.step3Description",
                    "Upload a clear, valid copy of your medical license. This helps us ensure that all professionals on our platform are verified."
                  )}
                </p>
              </div>

              <div className="rf3-form">
                <div
                  className={`rf3-upload${file ? " rf3-upload--has-file" : ""}`}
                  onDrop={onDrop}
                  onDragOver={onDragOver}
                  role="region"
                  aria-label="License image upload"
                >
                  {file && previewUrl ? (
                    <div className="rf3-preview-wrap">
                      <button
                        type="button"
                        className="rf3-preview-close"
                        onClick={handleRemove}
                        aria-label="Remove image"
                      >
                        <CloseOutlined />
                      </button>
                      <img src={previewUrl} alt="License preview" className="rf3-preview" />
                    </div>
                  ) : (
                    <div className="rf3-upload-content">
                      <div className="rf3-icon">
                        <img src={uploadIcon} alt="" aria-hidden />
                      </div>
                      <p className="rf3-upload-title">{t("signup.doctor.uploadTitle")}</p>
                      <p className="rf3-upload-subtitle">{t("signup.doctor.uploadSubtitle")}</p>
                      <div className="rf3-upload-btn-wrap">
                        <label className="rf3-upload-btn">
                          {t("signup.doctor.uploadButton")}
                          <input
                            type="file"
                            accept="image/jpeg,image/png"
                            onChange={onChangeInput}
                            hidden
                          />
                        </label>
                      </div>
                    </div>
                  )}
                </div>

                {error && (
                  <span className="rf3-error" role="alert">
                    {error}
                  </span>
                )}
              </div>
            </div>

            <div>
              <div className="rf3-bottom-row">
                <button
                  type="button"
                  className="Next-rf3"
                  disabled={!isPage3Valid}
                  onClick={() => navigate("/register4", { state: { licenceImg: file } })}
                >
                  {t("commonn.continue", "Continue")}
                </button>
              </div>

              <div className="signup-login-link-wrapper">
                <span className="signup-login-text">
                  {t("signup.alreadyHaveAccount2", "You already have an active account ?")}{" "}
                  <span className="signup-login-link" onClick={() => navigate("/login")}>
                    {t("commonn.signInNow", "Sign in now !")}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}